import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/_core/hooks/useAuth";
import { useI18n } from "@/lib/i18n";
import { useActiveWorkspace } from "@/contexts/ActiveWorkspace";
import { trpc } from "@/lib/trpc";
import { weekStartUTC, weekKey } from "@shared/workweek";
import {
  initialWeekOffsetFromUrl,
  initialWeekTargetFromUrl,
  syncWeekUrlParam,
  weekOffsetForTarget,
} from "@/lib/weekParam";
import { TabScaffold, mergeTabQueries, type TabQueryLike } from "@/components/tabs/TabScaffold";
import { AppTopBar } from "@/components/home/AppTopBar";
import { TeamPulseCard } from "@/components/team/TeamPulseCard";
import { SharedReceiptsFeed } from "@/components/team/SharedReceiptsFeed";
import { TeamContextCard } from "@/components/team/TeamContextCard";
import { AskTeamDialog } from "@/components/AskTeamDialog";
import { TeamDirectionSection } from "@/components/team/TeamDirectionSection";
import { WeeklyReportWizard } from "@/components/team/WeeklyReportWizard";
import { Button } from "@/components/ui/button";
import { Users, ChevronLeft, ChevronRight, RotateCcw, ShieldCheck, ArrowLeft, MessageCircleQuestion } from "lucide-react";

const NOW = new Date();

/**
 * Team tab — the Phase 2 reference implementation of the thin-client pattern.
 * Consumes ONLY the already-safe server endpoints (team.pulse,
 * team.sharedReceipts) and renders them. Computes NO sensitive value.
 */
export default function Team() {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const { isAuthenticated } = useAuth();
  const ws = useActiveWorkspace();
  const teamId = ws.active?.team.id ?? null;
  const teamName = ws.active?.team.name;
  // A personal workspace IS a team row (isPersonal=true) with only the owner,
  // so teamId is NOT null — but there's no team audience. Show a personal
  // empty state instead of a Team Pulse / shared feed "of one".
  const isPersonal = !!ws.active?.team.isPersonal;

  const prefs = trpc.preferences.me.useQuery(undefined, { enabled: isAuthenticated });
  const weekStartDay = prefs.data?.weekStartDay ?? 0;
  // Which week to ask the server about — a navigation input, NOT a sensitive
  // derivation (the server classifies + scopes everything: feed eligibility,
  // small-N suppression, sharing levels, viewer gating all stay server-side
  // regardless of the requested week). Supports `/team?week=YYYY-MM-DD`.
  const [weekOffset, setWeekOffset] = useState(initialWeekOffsetFromUrl);
  // The raw ?week= target, captured BEFORE the sync effect can rewrite the
  // URL. The Sunday-based initial offset above is a first guess only: for
  // weekStartDay > 0 it decodes one week early whenever todayUTCDay <
  // weekStartDay, which the Team ⇄ Report round-trip would compound. Once the
  // preference is known, re-derive the EXACT offset against the same base.
  const [initialWeekTarget] = useState(initialWeekTargetFromUrl);
  useEffect(() => {
    if (initialWeekTarget == null) return;
    setWeekOffset(weekOffsetForTarget(initialWeekTarget, weekStartDay, NOW));
  }, [initialWeekTarget, weekStartDay]);
  const weekStart = useMemo(() => {
    const b = weekStartUTC(NOW, weekStartDay);
    b.setUTCDate(b.getUTCDate() + weekOffset * 7);
    return b;
  }, [weekStartDay, weekOffset]);
  // Team Ask is a SECONDARY action: a question about the shared team context,
  // not a general chatbot. Dialog state lives here so the entry point can stay a
  // small row rather than another card.
  const [askOpen, setAskOpen] = useState(false);
  const weekStartKey = useMemo(
    () => weekKey(weekStart, weekStartDay),
    [weekStart, weekStartDay],
  );
  // URL follows the viewed week, so the report link, its Back link, and
  // browser back/forward all preserve the selection.
  useEffect(() => syncWeekUrlParam(weekStartKey), [weekStartKey]);
  const isCurrentWeek = weekOffset === 0;

  // Contextual updated-draft flow. When the report page's "Create updated
  // draft" sends the lead here (?reportFlow=updated-draft) we surface an
  // explanatory banner and bring the Weekly Report wizard into focus, so the
  // lead knows WHY they are on Team and how to return. Captured ONCE at mount;
  // it is a transient UI hint only — never sent to the server, never persisted,
  // never carries private data, and never shown to a member.
  const isAuthority =
    ws.active?.role === "owner" || ws.active?.role === "manager" || ws.active?.role === "lead";
  const [reportFlow] = useState<string | null>(() =>
    typeof window !== "undefined" && /[?&]reportFlow=updated-draft(?:&|$)/.test(window.location.search)
      ? "updated-draft"
      : null,
  );
  // Captured above; strip it from the URL once so the hint is TRULY transient —
  // it does not replay on reload or survive week navigation. `week` untouched.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (!url.searchParams.has("reportFlow")) return;
    url.searchParams.delete("reportFlow");
    window.history.replaceState(window.history.state, "", url);
  }, []);
  const showUpdatedDraftFlow = reportFlow === "updated-draft" && isAuthority && !isPersonal;
  // Scroll the wizard into view once, when the flow is active and it mounts.
  const scrolledToWizardRef = useRef(false);
  const wizardFlowRef = (node: HTMLDivElement | null) => {
    if (!node || !showUpdatedDraftFlow || scrolledToWizardRef.current) return;
    scrolledToWizardRef.current = true;
    node.scrollIntoView({ block: "start", behavior: "smooth" });
  };

  const weekLabel = useMemo(() => {
    const end = new Date(weekStart);
    end.setUTCDate(weekStart.getUTCDate() + 6);
    const f = (d: Date) =>
      d.toLocaleDateString(isAr ? "ar-u-nu-latn" : "en", { month: "short", day: "numeric", timeZone: "UTC" });
    return `${f(weekStart)} — ${f(end)}`;
  }, [weekStart, isAr]);

  const weekNav = (
    <div className="flex items-center gap-2">
      <button
        onClick={() => setWeekOffset(o => o - 1)}
        aria-label={isAr ? "الأسبوع السابق" : "Previous week"}
        className="rounded-full p-1 hover:bg-muted"
      >
        <ChevronLeft className="h-4 w-4" />
      </button>
      <span className="tabular-nums">{weekLabel}</span>
      <button
        onClick={() => setWeekOffset(o => o + 1)}
        aria-label={isAr ? "الأسبوع التالي" : "Next week"}
        className="rounded-full p-1 hover:bg-muted"
      >
        <ChevronRight className="h-4 w-4" />
      </button>
      {!isCurrentWeek ? (
        <button
          onClick={() => setWeekOffset(0)}
          className="ms-1 inline-flex items-center gap-1 rounded-full bg-coral-soft/60 text-coral px-2.5 py-0.5 text-xs font-medium hover:bg-coral-soft"
        >
          <RotateCcw className="h-3 w-3" /> {isAr ? "هذا الأسبوع" : "This week"}
        </button>
      ) : null}
    </div>
  );

  // Don't query team.pulse / team.sharedReceipts for a personal workspace —
  // there's no team to summarize.
  const enabled = isAuthenticated && teamId != null && !isPersonal;
  const pulse = trpc.team.pulse.useQuery({ teamId: teamId!, weekStartKey }, { enabled });
  const feed = trpc.team.sharedReceipts.useQuery({ teamId: teamId!, weekStartKey }, { enabled });
  // Stage 2A + Stage 1 (weekly operating model) — shared team direction + the
  // caller's OWN private relevance marks. Both are PERSISTENT: neither query
  // passes `period`, so Team Direction and its relevance marks stay visible
  // across week navigation (the reported bug was week-scoping these reads).
  // Both stay server-safe: list carries no createdByUserId; mine returns only
  // the caller's own links. No counts/momentum anywhere.
  const teamPrios = trpc.teamPriorities.list.useQuery(
    { teamId: teamId! },
    { enabled },
  );
  const myLinks = trpc.priorityLinks.mine.useQuery(
    { teamId: teamId! },
    { enabled },
  );
  // Stage 2 (weekly operating model) — Weekly Focus: the shared, week-specific
  // marker layered on the persistent direction. WEEK-SCOPED (weekStartKey in
  // the key), so it re-scopes on week nav while the direction list stays put.
  // Shared team data only (no userId/member/counts); never inferred from
  // private work.
  const weeklyFocus = trpc.weeklyFocus.list.useQuery(
    { teamId: teamId!, weekStartKey },
    { enabled },
  );
  // Stage 3 (weekly operating model) — Weekly Team Update: the human-authored
  // shared progress narrative for the VIEWED week. WEEK-SCOPED like Weekly
  // Focus; explicitly authored (never inferred from private work); v1
  // attribution is timestamp-only.
  const weeklyUpdate = trpc.weeklyTeamUpdates.get.useQuery(
    { teamId: teamId!, weekStartKey },
    { enabled },
  );
  // Phase W2 — per-priority weekly declarations for the VIEWED week. Shared
  // team data (sanitized: no author identity, no counts); WEEK-SCOPED like
  // Weekly Focus. Writes live in TeamDirectionSection (authority-only).
  const declarations = trpc.teamPriorityWeeklyDeclarations.list.useQuery(
    { teamId: teamId!, weekStartKey },
    { enabled },
  );
  // Phase W1 — the single Weekly Report workflow reads the role-projected
  // report state for the viewed week. A MEMBER payload is exactly
  // { status, approved } (no draft/stale/snapshot/fingerprint/generationMeta);
  // the server projection is the boundary, so members never receive wizard
  // state to render.
  const report = trpc.weeklyTeamReports.get.useQuery(
    { teamId: teamId!, weekStartKey },
    { enabled },
  );
  const state = mergeTabQueries({
    pulse,
    feed,
    teamPrios,
    myLinks,
    weeklyFocus,
    weeklyUpdate,
    declarations,
    report,
  });

  const sharedPill = (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-coral-soft/60 text-coral px-3 py-1 text-xs font-medium">
      <Users className="h-3.5 w-3.5" /> {isAr ? "مُشارَك" : "Shared"}
    </span>
  );

  // No team selected, OR the active workspace is PERSONAL (a solo workspace
  // with no team audience) → neutral empty, not a spinner / team-of-one.
  if (isAuthenticated && (teamId == null || isPersonal)) {
    const noTeam: TabQueryLike<unknown> = {
      isLoading: false,
      isError: false,
      data: {},
      refetch: () => {},
    };
    // The user's team workspaces (everything that isn't their personal space).
    // When they have at least one, the empty state offers a one-tap switch INTO
    // it so the Team tab is never a dead end; when they have none, it says so
    // honestly (no self-serve team creation exists yet).
    const teamWorkspaces = ws.workspaces.filter(w => !w.team.isPersonal);
    const hasTeamWorkspace = teamWorkspaces.length > 0;
    return (
      <TabScaffold
        title={isAr ? "الفريق" : "Team"}
        topBar={<AppTopBar />}
        state={noTeam}
        isEmpty={() => true}
        emptyTitle={
          isPersonal
            ? isAr ? "مساحتك الشخصية" : "Your personal workspace"
            : isAr ? "لم يتم اختيار فريق" : "No team selected"
        }
        emptyHint={
          hasTeamWorkspace
            ? isPersonal
              ? isAr
                ? "لا فريق هنا، أنت فقط. انتقل إلى مساحة عمل فريق لرؤية نبض الفريق والملخّصات المُشارَكة."
                : "It’s just you here, no team feed or pulse. Switch to your team to see Team Pulse and shared receipts."
              : isAr
                ? "انتقل إلى مساحة عمل فريق لرؤية نبض الفريق والملخّصات المُشارَكة."
                : "Switch to a team workspace to see Team Pulse and Shared Receipts."
            : isAr
              ? "أنت فقط هنا. مساحتك الشخصية خاصة بك. لست ضمن أي فريق بعد؛ عند إضافتك إلى فريق سيظهر هنا."
              : "It’s just you here. Your personal space is private. You’re not in a team yet; when you’re added to one, it’ll appear here."
        }
        emptyAction={
          hasTeamWorkspace
            ? teamWorkspaces.map(w => (
                <Button
                  key={w.team.id}
                  onClick={() => ws.setActiveId(w.team.id)}
                  className="rounded-full"
                  data-testid="team-switch"
                >
                  <Users className="h-4 w-4 me-1.5" />
                  {isAr ? `الانتقال إلى ${w.team.name}` : `Switch to ${w.team.name}`}
                </Button>
              ))
            : undefined
        }
      >
        {() => null}
      </TabScaffold>
    );
  }

  return (
    <TabScaffold
      title={isAr ? "الفريق" : "Team"}
        topBar={<AppTopBar />}
      subtitle={
        <div className="flex flex-col gap-0.5">
          {teamName ? <span>{teamName}</span> : null}
          {weekNav}
        </div>
      }
      headerRight={sharedPill}
      state={state}
      isEmpty={d =>
        d.feed.rows.length === 0 && d.pulse.suppressed && d.teamPrios.length === 0
      }
      emptyTitle={
        isCurrentWeek
          ? isAr ? "لا ملخّصات مُشارَكة بعد" : "No shared receipts yet"
          : isAr ? "لا شيء مُشارَك في ذلك الأسبوع" : "Nothing shared that week"
      }
      emptyHint={
        isCurrentWeek
          ? isAr
            ? "عندما يعتمد زملاؤك ملخّصاتهم ويشاركونها، ستظهر هنا."
            : "When teammates approve & share their receipts, they’ll appear here."
          : isAr
            ? "لم تتم مشاركة أي ملخّصات معتمدة في هذا الأسبوع."
            : "No approved receipts were shared in this week."
      }
    >
      {d => (
        <>
          {/* Team CONTEXT — which team, your role, who's on it, what's shared
              vs private. Shared context only; carries no private Priority
              Center activity. teamName + role come from the active workspace
              (no fetch); the roster loads lazily on expand. */}
          {teamName && ws.active ? (
            <TeamContextCard
              teamId={teamId!}
              teamName={teamName}
              role={ws.active.role}
              isAr={isAr}
            />
          ) : null}
          <TeamDirectionSection
            teamId={teamId!}
            period={weekStartKey}
            isAr={isAr}
            priorities={d.teamPrios}
            myLinks={d.myLinks}
            weeklyFocus={d.weeklyFocus}
            declarations={d.declarations}
            // Phase W1 — Weekly Focus is SET inside the wizard (Step 1); the
            // cards show a read-only "focused this week" badge only.
            focusReadOnly
            // Phase W2 — per-priority declarations are now DECLARED inside the
            // wizard (Step 2); the cards show a read-only status badge only.
            declarationsReadOnly
          />
          {/* Phase W1 — THE single, role-aware Weekly Report workflow. Replaces
              the old competing Team-page surfaces (standalone "Additional
              weekly context" card, read-only readiness card, and the "Review
              weekly report" row). Authority sees the four-step wizard; members
              receive + render only a compact outcome card (server payload is
              already role-split). */}
          <div ref={wizardFlowRef}>
            {/* Contextual updated-draft banner — authority-only, shown when the
                report page routed here to prepare an updated draft. Explains why
                the lead is on Team and offers a deliberate return path. The
                approved report stays visible until the updated draft is approved. */}
            {showUpdatedDraftFlow ? (
              <div
                className="mb-2.5 rounded-2xl border border-primary/40 bg-primary/[0.04] p-4"
                data-testid="team-updated-draft-flow"
                role="status"
              >
                <h2 className="text-sm font-semibold">
                  {isAr ? "إعداد مسودة محدَّثة" : "Preparing an updated draft"}
                </h2>
                <p className="mt-1 text-xs text-muted-foreground">
                  {isAr
                    ? "راجع أو غيّر مدخلات التقرير أدناه. سيبقى التقرير المعتمد ظاهرًا حتى اعتماد المسودة المحدَّثة."
                    : "Review or change the report inputs below. The approved report will remain visible until the updated draft is approved."}
                </p>
                <a
                  href={`/team-report?week=${weekStartKey}`}
                  data-testid="team-updated-draft-back"
                  className="mt-2.5 inline-flex items-center gap-1.5 rounded-full bg-muted px-3 py-1 text-xs font-medium hover:bg-muted/70"
                >
                  <ArrowLeft className="h-3.5 w-3.5" />
                  {isAr ? "العودة إلى التقرير المعتمد" : "Back to approved report"}
                </a>
              </div>
            ) : null}
            <WeeklyReportWizard
              teamId={teamId!}
              weekStartKey={weekStartKey}
              isAr={isAr}
              role={ws.active?.role}
              activeDirections={(d.teamPrios ?? [])
                .filter((p: any) => p.teamStatus === "active")
                .map((p: any) => ({ id: p.id, title: p.title }))}
              weeklyFocus={d.weeklyFocus ?? []}
              declarations={d.declarations ?? []}
              weeklyUpdate={d.weeklyUpdate ?? null}
              report={d.report}
            />
          </div>
          <TeamPulseCard pulse={d.pulse} />
          <SharedReceiptsFeed rows={d.feed.rows} />
          {/* Ask team — deliberately AFTER the report content and beside the
              private-by-design footer, so the scope statement is read in the same
              glance. Not on Home, not a primary navigation item. */}
          <button
            type="button"
            data-testid="team-ask-open"
            onClick={() => setAskOpen(true)}
            className="flex w-full items-start gap-2 rounded-2xl border border-border/60 bg-card px-4 py-3 text-start hover:bg-accent/40"
          >
            <MessageCircleQuestion className="h-4 w-4 shrink-0 mt-0.5 text-coral" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block text-sm font-medium">
                {isAr ? "اسأل الفريق" : "Ask team"}
              </span>
              <span className="block text-xs text-muted-foreground break-words">
                {isAr
                  ? "اطرح سؤالاً باستخدام سياق الفريق المسموح لـ Receipt باستخدامه."
                  : "Ask a question using the team context Receipt is allowed to use."}
              </span>
            </span>
          </button>
          <AskTeamDialog
            open={askOpen}
            onOpenChange={setAskOpen}
            weekStartKey={weekStartKey}
            weekLabel={weekLabel}
            teamId={teamId}
          />
          {/* Private-by-design footer — the shared-only promise, kept literally
              accurate by the bidirectional source-copy contract. W2 wording:
              declarations are now the primary progress source. */}
          <p
            data-testid="team-private-footer"
            className="flex items-start gap-2 rounded-2xl border border-border/60 bg-muted/20 px-4 py-3 text-[11px] text-muted-foreground"
          >
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 mt-0.5 text-coral" />
            <span>
              {isAr
                ? "يُستخدم فقط توجيه الفريق المُشارَك والتقدّم المُعلَن بشريًا والملخّصات المعتمدة. لا يُدرَج أي شيء خاص أو شخصي."
                : "Only shared team direction, human-declared progress and approved receipts are used. Nothing private or personal is included."}
            </span>
          </p>
          {/* Mobile-polish: extra breathing room so Team Pulse + Shared
              Receipts are never crowded against the fixed bottom nav / Capture
              button (on top of the shell's global nav reservation). */}
          <div className="pb-4" data-testid="team-bottom-spacer" aria-hidden />
        </>
      )}
    </TabScaffold>
  );
}
